import { Link } from "react-router-dom";

import { cn } from "../../design-system/cn.js";
import { describeRoles, type FeedUse } from "./usage.js";

/**
 * Which feeds use this object.
 *
 * Three feeds on one playlist is the ordinary case the primitives describe, so it is
 * drawn as an ordinary list of names — no warning tone, no count that reads as a
 * problem, no "shared" badge. A playlist nothing plays yet says so just as plainly.
 */
export function UsedBy({
  className,
  emptyLabel = "No feed uses this yet",
  showRoles = false,
  uses,
}: {
  className?: string;
  emptyLabel?: string;
  showRoles?: boolean;
  uses: readonly FeedUse[];
}) {
  if (uses.length === 0) {
    return <p className={cn("text-xs text-ink-3", className)}>{emptyLabel}</p>;
  }
  return (
    <ul className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {uses.map((use) => (
        <li key={use.feedId}>
          <Link
            className={cn(
              "flex items-center gap-1.5 rounded-full border border-border bg-bg-2 py-1 pr-2.5 pl-2 text-xs transition-colors hover:border-accent-line hover:text-ink",
              use.feedEnabled ? "text-ink-2" : "text-ink-3",
            )}
            title={use.feedEnabled ? undefined : `${use.feedName} (off)`}
            to={`/feeds/${encodeURIComponent(use.feedId)}`}
          >
            <span
              aria-hidden="true"
              className={cn(
                "size-1.5 shrink-0 rounded-full",
                use.feedEnabled ? "bg-ok" : "bg-ink-3",
              )}
            />
            <span className="max-w-[12rem] truncate">{use.feedName}</span>
            {showRoles && use.roles.length > 0 ? (
              <span className="font-mono text-[0.5625rem] tracking-[0.06em] text-ink-3 uppercase">
                {describeRoles(use.roles)}
              </span>
            ) : null}
          </Link>
        </li>
      ))}
    </ul>
  );
}
